import { useCallback } from 'react';

import { IdFilterType } from '../../../../../types';
import {
  CategoryID,
  CategoryLocalizedName
} from '../../../../categories/categoriesTypes';
import {
  FetchLifestylesFilters,
  LifestyleClientID,
  ChangeLifestylesFiltersFunc,
  FetchLifestylesFilterLifestyles,
  LifestyleCategoryID,
  FetchLifestylesFilterFields
} from '../../../../lifestyles/lifestylesTypes';
import { TeamUUID } from '../../../../teams/teamsTypes';

import { useCurrentUser } from '../../../../../auth/hooks/useAuth';

import { ThreeDStockShowFiltersLayout } from '../../layouts/ThreeDStockShowFiltersLayout';

import { ThreeDStockLifestylesFiltersAppliedFilters } from './components/ThreeDStockLifestylesFiltersAppliedFilters';
import { ThreeDStockLifestylesFiltersCategoriesFilter } from './components/ThreeDStockLifestylesFiltersCategoriesFilter';
import { ThreeDStockLifestylesFiltersFavoritesFilter } from './components/ThreeDStockLifestylesFiltersFavoritesFilter';
import { ThreeDStockLifestylesFiltersImageFilterBadge } from './components/ThreeDStockLifestylesFiltersImageFilterBadge';
import { ThreeDStockLifestylesFiltersLifestyleFilterBadge } from './components/ThreeDStockLifestylesFiltersLifestyleFilterBadge';

import { LifestylesMaterialsFilter } from '../../../../lifestyles/components/filters/LifestylesMaterialsFilter';
import { LifestylesStylesFilter } from '../../../../lifestyles/components/filters/LifestylesStylesFilter';
import { LifestylesColorsFilter } from '../../../../lifestyles/components/filters/LifestylesColorsFilter';
import { LifestylesBlockedFilter } from '../../../../lifestyles/components/filters/LifestylesBlockedFilter';
import { LifestylesTypesFilter } from '../../../../lifestyles/components/filters/LifestylesTypesFilter';
import { LifestylesGammasFilter } from '../../../../lifestyles/components/filters/LifestylesGammasFilter';
import { LifestylesTonesFilter } from '../../../../lifestyles/components/filters/LifestylesTonesFilter';
import { LifestylesPremiumContentAtFilter } from '../../../../lifestyles/components/filters/LifestylesPremiumContentAtFilter';

import { Translate } from '../../../../../helpers/Translate';
import { PureIconButtonHelper } from '../../../../../helpers/buttons/PureIconButtonHelper';
import { CheckPermissions } from '../../../../../helpers/CheckPermissions';

import { IconsEnum } from '../../../../../assets/icons/types';

import { LifestylesPermissions } from '../../../../lifestyles/lifestylesConstants';

import { words } from '../../../../../locales/keys';

interface ThreeDStockLifestylesFiltersProps {
  teamUuid?: TeamUUID;
  clientIds?: LifestyleClientID[];
  lifestylesFilters: FetchLifestylesFilters;
  defaultLifestyleCategoryId?: IdFilterType;
  defaultLifestyleParentCategoryIds?: LifestyleCategoryID[];
  changeLifestylesFilters: ChangeLifestylesFiltersFunc;
  filterLifestyles: FetchLifestylesFilterLifestyles;
  closeFilters?: () => void;
  clearFilters?: (persistFiltersFields?: FetchLifestylesFilterFields[]) => void;
  checkedCategories: { id: CategoryID; localizedName: CategoryLocalizedName }[];
  setCheckedCategories: (
    categories: { id: CategoryID; localizedName: CategoryLocalizedName }[]
  ) => void;
  changeCategory: (
    changeFilters?: FetchLifestylesFilters,
    removeFilters?: string[]
  ) => void;
  withPremiumContentAtFilter?: boolean;
  isModal?: boolean;
}

function ThreeDStockLifestylesFilters({
  teamUuid,
  clientIds,
  lifestylesFilters,
  changeLifestylesFilters,
  filterLifestyles,
  closeFilters,
  clearFilters,
  defaultLifestyleCategoryId,
  defaultLifestyleParentCategoryIds,
  checkedCategories,
  setCheckedCategories,
  changeCategory,
  withPremiumContentAtFilter,
  isModal
}: ThreeDStockLifestylesFiltersProps) {
  const currentUser = useCurrentUser();

  const handleClearFilters = useCallback<() => void>(() => {
    clearFilters();
    setCheckedCategories([]);
  }, [clearFilters, setCheckedCategories]);

  const handleChangeCategories = useCallback<ChangeLifestylesFiltersFunc>(
    (changedFilters, removeFilters) => {
      clearFilters([
        FetchLifestylesFilterFields.NAME_TAGS,
        FetchLifestylesFilterFields.SEARCH_GLOBAL_BY_LIFESTYLE
      ]);
      changeLifestylesFilters(changedFilters, removeFilters);
    },
    [changeLifestylesFilters, clearFilters]
  );

  return (
    <ThreeDStockShowFiltersLayout onClose={closeFilters} isModal={isModal}>
      <div className="h-full flex flex-col">
        <div className="flex justify-between pl-4 pr-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-t-xl py-1.5">
          <div className="text-sm uppercase font-semibold leading-8">
            <Translate id={words.refineSearch} />
          </div>

          <PureIconButtonHelper
            className="py-1.5 pl-1.5 pr-1.5 rounded-full inline-flex items-center leading-5 focus:ring-base hover:text-gray-950 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 focus:ring-offset-0 text-gray-500 dark:text-gray-400 transition-colors duration-150 ease-out"
            onClick={closeFilters}
            icon={IconsEnum.X}
            iconClassName="h-5 w-5"
            dataGa="close-filters"
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          <div id="filters-lifestyles" className="flex flex-col space-y-6">
            <ThreeDStockLifestylesFiltersImageFilterBadge
              changeLifestylesFilters={changeLifestylesFilters}
              lifestylesFilters={lifestylesFilters}
            />

            <ThreeDStockLifestylesFiltersLifestyleFilterBadge
              changeLifestylesFilters={changeLifestylesFilters}
              lifestylesFilters={lifestylesFilters}
            />

            <ThreeDStockLifestylesFiltersAppliedFilters
              changeLifestylesFilters={changeLifestylesFilters}
              lifestylesFilters={lifestylesFilters}
              filterLifestyles={filterLifestyles}
              checkedCategories={checkedCategories}
              setCheckedCategories={setCheckedCategories}
              clearFilters={handleClearFilters}
              defaultLifestyleCategoryId={defaultLifestyleCategoryId}
              defaultLifestyleParentCategoryIds={
                defaultLifestyleParentCategoryIds
              }
            />

            <CheckPermissions
              action={LifestylesPermissions.READ_LIFESTYLES_CATEGORIES_FILTER}
            >
              <ThreeDStockLifestylesFiltersCategoriesFilter
                lifestylesFilters={lifestylesFilters}
                changeLifestylesFilters={handleChangeCategories}
                defaultLifestyleCategoryId={defaultLifestyleCategoryId}
                checkedCategories={checkedCategories}
                setCheckedCategories={setCheckedCategories}
                changeCategory={changeCategory}
              />
            </CheckPermissions>

            <ThreeDStockLifestylesFiltersFavoritesFilter
              teamUuid={teamUuid}
              changeLifestylesFilters={changeLifestylesFilters}
              lifestylesFilters={lifestylesFilters}
            />

            <CheckPermissions
              action={
                LifestylesPermissions.READ_LIFESTYLES_PREMIUM_CONTENT_AT_FILTER
              }
            >
              {withPremiumContentAtFilter && (
                <LifestylesPremiumContentAtFilter
                  lifestylesFilters={lifestylesFilters}
                  changeLifestylesFilters={changeLifestylesFilters}
                  onlyPremiumOption={
                    !currentUser.hasPermissions(
                      LifestylesPermissions.READ_LIFESTYLES_PREMIUM_CONTENT_AT_FILTER_FREE_FIELD
                    )
                  }
                />
              )}
            </CheckPermissions>

            <CheckPermissions
              action={LifestylesPermissions.READ_LIFESTYLES_MATERIALS_FILTER}
            >
              <LifestylesMaterialsFilter
                clientIds={clientIds}
                lifestylesFilters={lifestylesFilters}
                changeLifestylesFilters={changeLifestylesFilters}
              />
            </CheckPermissions>

            <LifestylesStylesFilter
              clientIds={clientIds}
              lifestylesFilters={lifestylesFilters}
              changeLifestylesFilters={changeLifestylesFilters}
            />

            <LifestylesGammasFilter
              clientIds={clientIds}
              lifestylesFilters={lifestylesFilters}
              changeLifestylesFilters={changeLifestylesFilters}
            />

            <LifestylesTonesFilter
              clientIds={clientIds}
              lifestylesFilters={lifestylesFilters}
              changeLifestylesFilters={changeLifestylesFilters}
            />

            <CheckPermissions
              action={LifestylesPermissions.READ_LIFESTYLES_COLORS_FILTER}
            >
              <LifestylesColorsFilter
                clientIds={clientIds}
                lifestylesFilters={lifestylesFilters}
                changeLifestylesFilters={changeLifestylesFilters}
              />
            </CheckPermissions>

            <CheckPermissions
              action={LifestylesPermissions.READ_LIFESTYLES_BLOCKED_FILTER}
            >
              <LifestylesBlockedFilter
                lifestylesFilters={lifestylesFilters}
                changeLifestylesFilters={changeLifestylesFilters}
              />
            </CheckPermissions>

            <CheckPermissions
              action={LifestylesPermissions.READ_LIFESTYLES_TYPE_FILTER}
            >
              <LifestylesTypesFilter
                clientIds={clientIds}
                lifestylesFilters={lifestylesFilters}
                changeLifestylesFilters={changeLifestylesFilters}
              />
            </CheckPermissions>
          </div>
        </div>
      </div>
    </ThreeDStockShowFiltersLayout>
  );
}

export default ThreeDStockLifestylesFilters;
